import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isSameMonth, startOfDay } from 'date-fns';
import { lt } from 'date-fns/locale';
import { saveCourtPrices, listCourtPrices } from '../utils/firebaseUtils';

const WEEKDAY_LETTERS = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];
const WEEKDAY_SHORT = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];

const LOCATIONS = [
  { id: 'seb_arena', label: 'Seb arena' },
  { id: 'vrm_courts', label: 'VRM teniso kortai (Žirmūnų g. 1e, Vilnius)' },
  { id: 'bernardinai_courts', label: 'Bernardinų teniso kortai (B. Radvilaitės g. 6C, Vilnius)' },
];
const LOCATION_LABELS = {
  ...Object.fromEntries(LOCATIONS.map((l) => [l.id, l.label])),
  other: 'Kiti kortai',
  bernardinu_courts: 'Bernardinų teniso kortai (B. Radvilaitės g. 6C, Vilnius)',
};

// 7:00–22:00 (16 slots; last row 23:00–24:00 removed)
const getPriceTimeSlots = () => {
  const slots = [];
  for (let h = 7; h <= 22; h++) {
    slots.push(`${h.toString().padStart(2, '0')}:00`);
  }
  return slots;
};

const PRICE_TIER_COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#0288d1', '#c62828', '#558b2f', '#6a1b9a'];

export default function AdminCourtPricesPage() {
  const today = new Date();
  const [locationId, setLocationId] = useState('seb_arena');
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [displayMonth, setDisplayMonth] = useState(() => startOfMonth(today));
  const [priceInput, setPriceInput] = useState('');
  const [priceTiers, setPriceTiers] = useState([]); // [{ price: number, color: string }, ...]
  const [currentTierIndex, setCurrentTierIndex] = useState(null);
  const [slots, setSlots] = useState({}); // { "dayIndex_time": price (number) }
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [previousEntries, setPreviousEntries] = useState([]);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [viewDialogEntry, setViewDialogEntry] = useState(null);

  const loadPreviousEntries = useCallback(async () => {
    setLoadingPrevious(true);
    try {
      const list = await listCourtPrices();
      setPreviousEntries(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPrevious(false);
    }
  }, []);

  useEffect(() => {
    loadPreviousEntries();
  }, [loadPreviousEntries]);

  const validFrom = rangeStart && rangeEnd ? (rangeStart <= rangeEnd ? rangeStart : rangeEnd) : null;
  const validTo = rangeStart && rangeEnd ? (rangeStart <= rangeEnd ? rangeEnd : rangeStart) : null;

  const timeSlots = useMemo(() => getPriceTimeSlots(), []);

  const clearRange = () => {
    setRangeStart(null);
    setRangeEnd(null);
  };

  const handleRangeDayClick = (day) => {
    const hasStart = !!rangeStart;
    const hasEnd = !!rangeEnd;
    if (!hasStart && !hasEnd) {
      setRangeStart(startOfDay(day));
      setRangeEnd(null);
    } else if (hasStart && !hasEnd) {
      const start = rangeStart;
      const d = startOfDay(day);
      if (d < start) {
        setRangeStart(d);
        setRangeEnd(start);
      } else {
        setRangeEnd(d);
      }
    } else {
      clearRange();
    }
  };

  const renderRangeCalendar = () => {
    const monthStart = startOfMonth(displayMonth);
    const monthEnd = endOfMonth(displayMonth);
    const startPad = (monthStart.getDay() + 6) % 7;
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const leadingEmpty = Array(startPad).fill(null);
    const allCells = [...leadingEmpty, ...daysInMonth];
    const from = validFrom ? startOfDay(validFrom) : null;
    const to = validTo ? startOfDay(validTo) : null;
    const dayStart = (d) => startOfDay(d);
    const inRange = (d) => {
      if (rangeStart && !rangeEnd) return dayStart(d).getTime() === startOfDay(rangeStart).getTime();
      return from && to && dayStart(d) >= from && dayStart(d) <= to;
    };
    const isRangeEdge = (d) => {
      const t = dayStart(d).getTime();
      if (rangeStart && !rangeEnd) return t === startOfDay(rangeStart).getTime();
      if (!from || !to) return false;
      return t === from.getTime() || t === to.getTime();
    };
    return (
      <Paper sx={{ p: 2, minWidth: 200, maxWidth: 280 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Kainos galioja nuo – iki
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <IconButton size="small" onClick={() => setDisplayMonth((m) => subMonths(m, 1))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" fontWeight={600}>
            {format(displayMonth, 'MMMM yyyy', { locale: lt })}
          </Typography>
          <IconButton size="small" onClick={() => setDisplayMonth((m) => addMonths(m, 1))}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {WEEKDAY_LETTERS.map((d) => (
            <Box key={d} sx={{ width: 'calc(14.28% - 4px)', textAlign: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>
              {d}
            </Box>
          ))}
          {allCells.map((day, i) => {
            if (!day) {
              return <Box key={`e-${i}`} sx={{ width: 'calc(14.28% - 4px)', height: 32 }} />;
            }
            const inRangeGreen = inRange(day);
            const isEdge = isRangeEdge(day);
            const isCurrentMonth = isSameMonth(day, displayMonth);
            return (
              <Button
                key={day.toISOString()}
                size="small"
                onClick={() => handleRangeDayClick(day)}
                sx={{
                  minWidth: 0,
                  width: 'calc(14.28% - 4px)',
                  height: 32,
                  p: 0,
                  fontSize: '0.75rem',
                  bgcolor: inRangeGreen ? (isEdge ? 'success.dark' : 'success.main') : 'transparent',
                  color: inRangeGreen ? 'success.contrastText' : isCurrentMonth ? 'text.primary' : 'text.disabled',
                  outline: isEdge && inRangeGreen ? '2px solid' : 'none',
                  outlineColor: 'success.contrastText',
                  '&:hover': { bgcolor: inRangeGreen ? 'success.dark' : 'action.hover' }
                }}
              >
                {format(day, 'd')}
              </Button>
            );
          })}
        </Box>
      </Paper>
    );
  };

  const handleAddPrice = () => {
    const raw = priceInput.trim().replace(',', '.');
    const num = parseFloat(raw);
    if (Number.isNaN(num) || num < 0) {
      setError('Įveskite teisingą kainą (skaičius ≥ 0).');
      return;
    }
    setError('');
    const existing = priceTiers.findIndex((t) => t.price === num);
    if (existing >= 0) {
      setCurrentTierIndex(existing);
      return;
    }
    const color = PRICE_TIER_COLORS[priceTiers.length % PRICE_TIER_COLORS.length];
    setPriceTiers((prev) => [...prev, { price: num, color }]);
    setCurrentTierIndex(priceTiers.length);
  };

  const getSlotPrice = (dayIndex, time) => slots[`${dayIndex}_${time}`] ?? null;
  const getColorForPrice = (price) => {
    const tier = priceTiers.find((t) => t.price === price);
    return tier ? tier.color : 'grey.200';
  };

  const handleCellClick = (dayIndex, time) => {
    if (currentTierIndex == null) return;
    const price = priceTiers[currentTierIndex].price;
    const key = `${dayIndex}_${time}`;
    const current = slots[key];
    if (current === price) {
      setSlots((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      setSlots((prev) => ({ ...prev, [key]: price }));
    }
  };

  const handleSubmit = async () => {
    if (!validFrom || !validTo) {
      setError('Pasirinkite galiojimo laikotarpį (nuo – iki) kalendoriuje.');
      return;
    }
    setSubmitLoading(true);
    setError('');
    setSuccess(false);
    try {
      const fromStr = format(validFrom, 'yyyy-MM-dd');
      const toStr = format(validTo, 'yyyy-MM-dd');
      await saveCourtPrices(locationId, fromStr, toStr, slots);
      setSuccess(true);
      loadPreviousEntries();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Nepavyko išsaugoti.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const cellHeight = 28;

  return (
    <Container maxWidth="xl" sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
        Aikštelių kainos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
        Pasirinkite vietą, galiojimo datas ir kainas, pažymėkite savaitės langelius pagal kainas, tada Pateikti.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 560 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, width: '100%', maxWidth: 560 }} onClose={() => setSuccess(false)}>
          Kainos išsaugotos.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Vieta</InputLabel>
          <Select
            value={locationId}
            label="Vieta"
            onChange={(e) => setLocationId(e.target.value)}
          >
            {LOCATIONS.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>{loc.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {renderRangeCalendar()}
      </Box>

      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {validFrom && validTo
            ? <>Galioja nuo: <strong>{format(validFrom, 'yyyy-MM-dd', { locale: lt })}</strong> — iki: <strong>{format(validTo, 'yyyy-MM-dd', { locale: lt })}</strong></>
            : 'Pasirinkite datų intervalą kalendoriuje.'}
        </Typography>
        {validFrom && validTo && (
          <Button size="small" color="inherit" onClick={clearRange} sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}>
            Išvalyti
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 3, maxWidth: 560, width: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>Kaina (€) ir žymėjimas</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <TextField
            size="small"
            label="Kaina"
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={priceInput}
            onChange={(e) => setPriceInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddPrice()}
            sx={{ width: 100 }}
          />
          <Button variant="outlined" size="small" onClick={handleAddPrice}>
            Pridėti kainą
          </Button>
          {priceTiers.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Pasirinkite kainą žymėjimui:</Typography>
              {priceTiers.map((tier, idx) => (
                <Button
                  key={idx}
                  size="small"
                  variant={currentTierIndex === idx ? 'contained' : 'outlined'}
                  onClick={() => setCurrentTierIndex(idx)}
                  sx={{
                    minWidth: 48,
                    bgcolor: currentTierIndex === idx ? tier.color : undefined,
                    borderColor: tier.color,
                    color: currentTierIndex === idx ? 'white' : tier.color,
                    '&:hover': { bgcolor: currentTierIndex === idx ? tier.color : undefined, borderColor: tier.color }
                  }}
                >
                  {tier.price}€
                </Button>
              ))}
            </Box>
          )}
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          maxWidth: 960,
          mx: 'auto',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center' }}>
            Savaitės vaizdas (7:00–22:00). Spustelėkite langelį – priskiriama pasirinkta kaina.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <Box sx={{ width: 56, flexShrink: 0, borderRight: '2px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Box sx={{ height: 48, borderBottom: '2px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">Laikas</Typography>
            </Box>
            {timeSlots.map((time) => (
              <Box
                key={time}
                sx={{
                  height: cellHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.100',
                }}
              >
                {time}
              </Box>
            ))}
          </Box>

          {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
            <Box key={dayIndex} sx={{ flex: 1, minWidth: 0, borderRight: dayIndex < 6 ? '1px solid' : 'none', borderColor: 'divider' }}>
              <Box
                sx={{
                  height: 48,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  px: 0.5,
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ lineHeight: 1.2 }}>{WEEKDAY_SHORT[dayIndex]}</Typography>
              </Box>
              {timeSlots.map((time) => {
                const price = getSlotPrice(dayIndex, time);
                const bgColor = price != null ? getColorForPrice(price) : 'grey.50';
                const textColor = price != null ? 'white' : 'grey.500';
                return (
                  <Box
                    key={`${dayIndex}-${time}`}
                    component="button"
                    type="button"
                    onClick={() => handleCellClick(dayIndex, time)}
                    sx={{
                      width: '100%',
                      height: cellHeight,
                      bgcolor: bgColor,
                      color: textColor,
                      border: 'none',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      cursor: currentTierIndex != null ? 'pointer' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      '&:hover': { opacity: currentTierIndex != null ? 0.85 : 1 }
                    }}
                  >
                    {price != null ? `${price}€` : '—'}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'center', px: 2, py: 2, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          {priceTiers.map((tier, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: tier.color }} />
              <Typography variant="body2" fontWeight={500}>{tier.price}€</Typography>
            </Box>
          ))}
          <Button
            variant="outlined"
            onClick={() => setSlots({})}
            disabled={Object.keys(slots).length === 0}
          >
            Išvalyti langelius
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!validFrom || !validTo || submitLoading}
          >
            {submitLoading ? 'Siunčiama…' : 'Pateikti'}
          </Button>
        </Box>
      </Paper>

      {/* Previous entries table */}
      <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'center' }}>
        Ankstesni įrašai
      </Typography>
      <TableContainer component={Paper} sx={{ maxWidth: 720, width: '100%', mb: 3 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Įvedimo data</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Vieta</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Galioja nuo</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>Galioja iki</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', width: 120 }}>Veiksmai</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingPrevious ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : previousEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Ankstesnių įrašų nėra.
                </TableCell>
              </TableRow>
            ) : (
              previousEntries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    {entry.updatedAt?.toDate
                      ? format(entry.updatedAt.toDate(), 'yyyy-MM-dd HH:mm', { locale: lt })
                      : '—'}
                  </TableCell>
                  <TableCell>{LOCATION_LABELS[entry.locationId] ?? entry.locationId}</TableCell>
                  <TableCell>{entry.validFrom || '—'}</TableCell>
                  <TableCell>{entry.validTo || '—'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setViewDialogEntry(entry)}
                      sx={{ textTransform: 'none' }}
                    >
                      Peržiūrėti
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View dialog: calendar with prices (read-only) */}
      <Dialog
        open={Boolean(viewDialogEntry)}
        onClose={() => setViewDialogEntry(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {viewDialogEntry && (
          <>
            <DialogTitle sx={{ textAlign: 'center' }}>
              Kainos: {LOCATION_LABELS[viewDialogEntry.locationId] ?? viewDialogEntry.locationId} ({viewDialogEntry.validFrom} – {viewDialogEntry.validTo})
            </DialogTitle>
            <DialogContent>
              {(() => {
                const viewSlots = viewDialogEntry.slots || {};
                const uniquePrices = [...new Set(Object.values(viewSlots).filter((v) => typeof v === 'number'))].sort((a, b) => a - b);
                const viewTiers = uniquePrices.map((price, idx) => ({
                  price,
                  color: PRICE_TIER_COLORS[idx % PRICE_TIER_COLORS.length]
                }));
                const getViewColor = (price) => {
                  const t = viewTiers.find((x) => x.price === price);
                  return t ? t.color : 'grey.50';
                };
                return (
                  <Box>
                    <Box sx={{ display: 'flex', minWidth: 0 }}>
                      <Box sx={{ width: 56, flexShrink: 0, borderRight: '2px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                        <Box sx={{ height: 40, borderBottom: '2px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                          <Typography variant="caption" fontWeight={700} color="text.secondary">Laikas</Typography>
                        </Box>
                        {timeSlots.map((time) => (
                          <Box
                            key={time}
                            sx={{ height: cellHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.100' }}>
                            {time}
                          </Box>
                        ))}
                      </Box>
                      {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
                        <Box key={dayIndex} sx={{ flex: 1, minWidth: 0, borderRight: dayIndex < 6 ? '1px solid' : 'none', borderColor: 'divider' }}>
                          <Box sx={{ height: 40, borderBottom: '2px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', px: 0.5 }}>
                            <Typography variant="caption" fontWeight={700}>{WEEKDAY_SHORT[dayIndex]}</Typography>
                          </Box>
                          {timeSlots.map((time) => {
                            const price = viewSlots[`${dayIndex}_${time}`];
                            const bgColor = price != null ? getViewColor(price) : 'grey.50';
                            const textColor = price != null ? 'white' : 'grey.500';
                            return (
                              <Box
                                key={`${dayIndex}-${time}`}
                                sx={{
                                  width: '100%',
                                  height: cellHeight,
                                  bgcolor: bgColor,
                                  color: textColor,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {price != null ? `${price}€` : '—'}
                              </Box>
                            );
                          })}
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'center', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      {viewTiers.map((tier, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: tier.color }} />
                          <Typography variant="body2" fontWeight={500}>{tier.price}€</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Container>
  );
}
